import {
  addMonths,
  allocate,
  calculateEmployee,
  earlierBonus,
  monthOfDate,
  monthsBetween,
  periodText,
  roundHalfUp2,
  runAllocation,
} from "./bonusCalc";

// SAMPLE DATA for Payroll > Pay Inputs > Bonus.
//
// The backend's work-screen API (Bonus API doc "Payroll work screen —
// planned, not built", base /api/payroll/pay-inputs/bonus/) is not deployed
// yet (404 on 4 Nov 2026). This file stands in for it with the same calls:
//   runs/calculate/, runs/{id}/recalculate/, runs/, runs/{id}/,
//   runs/{id}/add-to-payroll/, runs/{id}/cancel/, entries/, entries/summary/,
//   entries/ POST/PATCH, entries/{id}/cancel/, entries/{id}/assign-cycle/,
//   import/preview/, import/commit/
// Swap each function for a fetch when the backend ships; the screens only
// use these exports.
//
// Bonus types and settings are REAL (Payroll Settings > Bonus API). Runs,
// entries, employees, monthly payroll and payroll cycles are sample data,
// kept in memory per company for the session. The rules (spec B5, B6, B9,
// B10) are applied here so the screens behave as specified.

const USER = "You";
const wait = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));
const clone = (value) => JSON.parse(JSON.stringify(value));
const nowIso = () => new Date().toISOString();

export class BonusSampleError extends Error {
  constructor(message, { code, meta } = {}) {
    super(message);
    this.code = code;
    this.meta = meta || {};
  }
}

// ---------- Sample payroll cycles ----------

const CYCLE_MONTHS = monthsBetween("2025-04", "2026-12");
const cycleName = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-GB", { month: "short", year: "numeric" });
const buildCycles = () =>
  CYCLE_MONTHS.map((m, i) => {
    const next = addMonths(m, 1);
    const last = new Date(Number(m.slice(0, 4)), Number(m.slice(5)), 0).getDate();
    return {
      id: 500 + i,
      month: m,
      name: cycleName(m),
      start: `01 ${cycleName(m)}`,
      end: `${last} ${cycleName(m)}`,
      pay_month: cycleName(next),
      // Up to Sep 2026 are processed and frozen; Oct 2026 is the current open
      // cycle.
      frozen: m < "2026-10",
    };
  });

export const cycleLabel = (cycle) => (cycle ? `${cycle.name} · paid ${cycle.pay_month}` : "Not assigned");
export const cycleDates = (cycle) => (cycle ? `${cycle.name} payroll cycle (${cycle.start.split(" ")[0]}–${cycle.end}, paid ${cycle.pay_month})` : "—");

// ---------- Sample employees and monthly payroll ----------

const MONTHS = monthsBetween("2024-04", "2026-12");

const makeEmployee = ({ id, code, name, site, department, joined = "2020-06-01", exited = null, month }) => ({
  id, code, name, site, department, joined, exited,
  monthly: Object.fromEntries(MONTHS.filter((m) => m <= "2026-10").map((m, i) => [m, { payrollFinal: true, attendanceComplete: true, ...month(m, i) }])),
});

const daysCycle = (list) => (m, i) => list[i % list.length];

const buildEmployees = () => {
  const raviDays = { "2025-04": 26, "2025-05": 25, "2025-06": 22, "2025-07": 26, "2025-08": 26, "2025-09": 25, "2025-10": 26, "2025-11": 25, "2025-12": 26, "2026-01": 25, "2026-02": 24, "2026-03": 26 };
  const anjaliDays = { "2025-04": 25, "2025-05": 25, "2025-06": 24, "2025-07": 25, "2025-08": 23, "2025-09": 25, "2025-10": 25, "2025-11": 25, "2025-12": 25, "2026-01": 25, "2026-02": 26, "2026-03": 25 };
  const plain = (basic, da, gross, days = daysCycle([26, 25, 26, 24, 26, 25])) => (m, i) => {
    const paidDays = days(m, i);
    return { basic, da, gross, ot: 0, paidDays, daysPresent: paidDays - 2 };
  };
  return [
    makeEmployee({
      id: 1021, code: "E1021", name: "Ravi Kumar", site: "Erode Plant", department: "Production",
      month: (m) => {
        const late = m >= "2025-10";
        const june = m === "2025-06";
        const basic = june ? 7200 : late ? 9500 : 9000;
        const da = june ? 2000 : 2500;
        const paidDays = raviDays[m] ?? 26;
        return { basic, da, gross: basic + da + 4500, ot: 1500, paidDays, daysPresent: paidDays - 2 };
      },
    }),
    makeEmployee({
      id: 1022, code: "E1022", name: "Anjali Sharma", site: "Erode Plant", department: "Stores",
      month: (m) => {
        const aug = m === "2025-08";
        const paidDays = anjaliDays[m] ?? 25;
        return { basic: aug ? 4500 : 5000, da: aug ? 1350 : 1500, gross: aug ? 8100 : 9000, ot: 0, paidDays, daysPresent: paidDays - 1 };
      },
    }),
    makeEmployee({ id: 1033, code: "E1033", name: "Suresh M", site: "Erode Plant", department: "Production", joined: "2025-05-12", month: plain(7000, 2000, 12000) }),
    makeEmployee({ id: 1107, code: "E1107", name: "Priya N", site: "Chennai Office", department: "Accounts", month: plain(4500, 1300, 8500) }),
    makeEmployee({
      id: 1140, code: "E1140", name: "Meena R", site: "Erode Plant", department: "Production",
      month: (m, i) => ({ basic: 9000, da: 2000, gross: m === "2025-07" ? 24000 : 19000, ot: 0, paidDays: [26, 25, 26][i % 3], daysPresent: 24 }),
    }),
    makeEmployee({ id: 1110, code: "E1110", name: "Karthik S", site: "Chennai Office", department: "Stores", joined: "2026-03-05", month: (m) => ({ basic: 6000, da: 1500, gross: 10000, ot: 0, paidDays: m === "2026-03" ? 22 : 26, daysPresent: m === "2026-03" ? 21 : 25 }) }),
    makeEmployee({
      id: 1150, code: "E1150", name: "Divya K", site: "Chennai Office", department: "HR",
      month: (m, i) => ({ ...plain(5500, 1500, 9500)(m, i), payrollFinal: m !== "2025-06" }),
    }),
    makeEmployee({ id: 1160, code: "E1160", name: "Arun Prakash", site: "Chennai Office", department: "Accounts", month: plain(6200, 1800, 11000) }),
    makeEmployee({ id: 1171, code: "E1171", name: "Lakshmi V", site: "Erode Plant", department: "Stores", month: plain(5600, 1400, 9800) }),
    makeEmployee({ id: 1185, code: "E1185", name: "Vijay R", site: "Erode Plant", department: "Production", exited: "2025-08-20", month: plain(6800, 1700, 11500) }),
    makeEmployee({ id: 1190, code: "E1190", name: "Kavya S", site: "Chennai Office", department: "HR", joined: "2025-11-03", month: plain(5900, 1600, 10500) }),
    makeEmployee({
      id: 1210, code: "E1210", name: "Deepa K", site: "Erode Plant", department: "Production",
      month: (m, i) => ({ ...plain(6400, 1600, 11200)(m, i), attendanceComplete: m !== "2026-01" }),
    }),
  ];
};

// ---------- Store (per company, in memory) ----------

const stores = new Map();

const findType = (types, name) => types.find((type) => String(type.name).toLowerCase() === name.toLowerCase());
const typeSnapshot = (type) => ({ id: type.id, name: type.name, version: type.version, method: type.method, is_statutory: type.is_statutory });

const nextRunId = (store, createdAt) => {
  const ym = createdAt.slice(0, 7);
  const count = store.runs.filter((run) => run.id.startsWith(`BR-${ym}`)).length + 1;
  return `BR-${ym}-${String(count).padStart(2, "0")}`;
};

const hashIds = (ids) => [...ids].sort((a, b) => a - b).join(",");

// Builds a run and its entries from calculated rows (used by the seeds and
// by Add to payroll).
const createRunRecords = (store, { type, from, to, payMode, partPercent, cycle, rows, name, createdAt, status = "added", entryStatus = "ready", rounding }) => {
  const runId = nextRunId(store, createdAt);
  const payable = rows.filter((row) => row.result === "eligible" && row.pay_now > 0);
  const entries = payable.map((row, i) => ({
    id: `${runId}-${i + 1}`,
    employee_id: row.employee_id,
    employee_name: row.employee_name,
    employee_code: row.employee_code,
    site: row.site,
    bonus_type_id: type.id,
    bonus_type_name: type.name,
    type_version: type.version,
    is_statutory: Boolean(type.is_statutory),
    period_from: from,
    period_to: to,
    amount: row.pay_now,
    source: "run",
    run_id: runId,
    pay_mode: payMode,
    part_percent: payMode === "part" ? Number(partPercent) : null,
    payroll_cycle_id: cycle?.id ?? null,
    status: entryStatus,
    created_at: createdAt,
    created_by: USER,
    remarks: "",
    calc: { ...clone(row), method: type.method, rate: type.rate, days_basis: type.days_basis },
    allocations: runAllocation(row),
    history: [{ at: createdAt, by: USER, text: `added bonus run ${runId} to ${cycle?.name || "—"} payroll` }],
  }));
  const run = {
    id: runId,
    bonus_type_id: type.id,
    bonus_type_name: type.name,
    type: typeSnapshot(type),
    type_version: type.version,
    rule_snapshot: { ...clone(type), rounding },
    from_month: from,
    to_month: to,
    pay_mode: payMode,
    part_percent: payMode === "part" ? Number(partPercent) : null,
    payroll_cycle_id: cycle?.id ?? null,
    name: name || "",
    status,
    rows: clone(rows),
    employee_hash: hashIds(rows.map((row) => row.employee_id)),
    created_at: createdAt,
    created_by: USER,
    cancel_reason: "",
  };
  store.runs.push(run);
  store.entries.push(...entries);
  return run;
};

const totalsOf = (rows) => ({
  selected: rows.length,
  eligible: rows.filter((row) => row.result === "eligible").length,
  excluded: rows.filter((row) => row.result === "excluded").length,
  check: rows.filter((row) => row.result === "check").length,
  due: roundHalfUp2(rows.filter((row) => row.result === "eligible").reduce((sum, row) => sum + (row.due || 0), 0)),
  already_paid: roundHalfUp2(rows.filter((row) => row.result === "eligible").reduce((sum, row) => sum + (row.already_paid || 0), 0)),
  pay_now: roundHalfUp2(rows.filter((row) => row.result === "eligible").reduce((sum, row) => sum + (row.pay_now || 0), 0)),
});

const seedStore = (store, types, rounding) => {
  const cycle = (m) => store.cycles.find((c) => c.month === m);
  const statutory = findType(types, "Statutory Bonus") || types.find((type) => type.method === "percent_of_wages");
  if (statutory) {
    // Jan 2026: part payment 25% for Apr–Dec 2025, already paid in salary.
    const rows = store.employees.map((employee) => calculateEmployee({
      employee, type: statutory, from: "2025-04", to: "2025-12", payMode: "part", partPercent: 25, rounding, entries: [],
    }));
    createRunRecords(store, {
      type: statutory, from: "2025-04", to: "2025-12", payMode: "part", partPercent: 25, cycle: cycle("2026-01"), rows,
      name: "FY 2025–26 bonus advance (Pongal)", createdAt: "2026-01-12T10:20:00", status: "processed", entryStatus: "processed", rounding,
    });
  }
  const festival = findType(types, "Festival Bonus") || types.find((type) => type.method === "fixed_or_days");
  if (festival) {
    const rows = store.employees.map((employee) => calculateEmployee({
      employee, type: festival, from: "2026-10", to: "2026-10", payMode: "full_balance", partPercent: 100, rounding, entries: store.entries,
    }));
    createRunRecords(store, { type: festival, from: "2026-10", to: "2026-10", payMode: "full_balance", cycle: cycle("2026-10"), rows, name: "Diwali festival bonus", createdAt: "2026-10-20T11:05:00", rounding });
  }
  const performance = findType(types, "Performance Bonus") || types.find((type) => type.method === "enter_amount");
  if (performance) {
    const employee = (id) => store.employees.find((e) => e.id === id);
    const manual = (id, values) => {
      const emp = employee(id);
      const entry = {
        id: `ME-${store.entries.length + 1}`,
        employee_id: emp.id, employee_name: emp.name, employee_code: emp.code, site: emp.site,
        bonus_type_id: performance.id, bonus_type_name: performance.name, type_version: performance.version, is_statutory: Boolean(performance.is_statutory),
        run_id: null, calc: null, remarks: "", external_ref: "", ...values,
      };
      entry.allocations = allocate(entry.amount, monthsBetween(entry.period_from, entry.period_to));
      if (entry.status === "cancelled") entry.allocations = {};
      store.entries.push(entry);
    };
    manual(1160, { period_from: "2026-07", period_to: "2026-09", amount: 3000, source: "manual", payroll_cycle_id: cycle("2026-10").id, status: "ready", created_at: "2026-10-18T15:10:00", created_by: USER, remarks: "Q2 target achieved", history: [{ at: "2026-10-18T15:10:00", by: USER, text: "added manually" }] });
    manual(1140, { period_from: "2026-07", period_to: "2026-09", amount: 5000, source: "import", import_ref: "bonus_q2.xlsx · row 14", payroll_cycle_id: null, status: "ready", created_at: "2026-10-18T16:20:00", created_by: USER, history: [{ at: "2026-10-18T16:20:00", by: USER, text: "imported (no payroll cycle in file; default treatment Defer)" }] });
    manual(1033, { period_from: "2026-07", period_to: "2026-09", amount: 2000, source: "import", import_ref: "bonus_q2.xlsx · row 9", payroll_cycle_id: null, status: "cancelled", cancel_reason: "Duplicate of a manual entry", created_at: "2026-10-17T12:00:00", created_by: USER, history: [{ at: "2026-10-17T12:00:00", by: USER, text: "imported" }, { at: "2026-10-17T12:30:00", by: USER, text: "cancelled — Duplicate of a manual entry" }] });
  }
};

const getStore = (companyId, types, rounding) => {
  const key = String(companyId);
  if (!stores.has(key)) {
    const store = { employees: buildEmployees(), cycles: buildCycles(), runs: [], entries: [] };
    stores.set(key, store);
    seedStore(store, types, rounding);
  }
  return stores.get(key);
};

// Run status follows its entries (spec B5): all non-cancelled Processed ->
// Processed; all cancelled -> Cancelled.
const syncRunStatus = (store, runId) => {
  const run = store.runs.find((r) => r.id === runId);
  if (!run || run.status === "draft") return;
  const entries = store.entries.filter((entry) => entry.run_id === runId);
  if (!entries.length) return;
  const live = entries.filter((entry) => entry.status !== "cancelled");
  if (!live.length) run.status = "cancelled";
  else if (live.every((entry) => entry.status === "processed")) run.status = "processed";
};

// ---------- Public API (sample) ----------

// Loads (or seeds) the company's sample data. types: real bonus types.
export const openBonusWorkspace = async ({ companyId, types, rounding }) => {
  await wait(150);
  const store = getStore(companyId, types, rounding);
  return { employees: clone(store.employees.map(({ monthly, ...rest }) => rest)), cycles: clone(store.cycles) };
};

const entryView = (store, entry, types) => {
  const cycle = store.cycles.find((c) => c.id === entry.payroll_cycle_id) || null;
  const run = entry.run_id ? store.runs.find((r) => r.id === entry.run_id) : null;
  const type = types.find((t) => t.id === entry.bonus_type_id);
  return {
    ...clone(entry),
    bonus_type_name: type?.name || entry.bonus_type_name,
    payroll_cycle: cycle,
    payroll_cycle_name: cycle?.name || "",
    deferred: entry.status === "ready" && !cycle,
    run_name: run?.name || "",
    run_pay_mode: run?.pay_mode,
    run_part_percent: run?.part_percent,
  };
};

// GET entries/ (all; the screen filters).
export const listBonusEntries = async ({ companyId, types }) => {
  await wait();
  const store = stores.get(String(companyId));
  return store ? store.entries.map((entry) => entryView(store, entry, types)).sort((a, b) => (a.created_at < b.created_at ? 1 : -1)) : [];
};

// GET runs/
export const listBonusRuns = async ({ companyId, types }) => {
  await wait();
  const store = stores.get(String(companyId));
  if (!store) return [];
  return store.runs
    .filter((run) => run.status !== "draft")
    .map((run) => {
      const entries = store.entries.filter((entry) => entry.run_id === run.id);
      const live = entries.filter((entry) => entry.status !== "cancelled");
      const cycle = store.cycles.find((c) => c.id === run.payroll_cycle_id) || null;
      const type = types.find((t) => t.id === run.bonus_type_id);
      return {
        ...clone(run),
        bonus_type_name: type?.name || run.bonus_type_name,
        payroll_cycle: cycle,
        employees: live.length,
        amount: roundHalfUp2(live.reduce((sum, entry) => sum + entry.amount, 0)),
        totals: totalsOf(run.rows),
        // Can be cancelled only while none of its entries is Included or
        // Processed (spec B5).
        can_cancel: run.status === "added" && !entries.some((entry) => entry.status === "included" || entry.status === "processed"),
      };
    })
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
};

// POST runs/calculate/ — Step 1 -> Step 2. Returns a draft (not saved to
// the runs list; nothing goes to payroll).
export const calculateBonusRun = async ({ companyId, type, from, to, payMode, partPercent, cycleId, employeeIds, rounding }) => {
  await wait(500);
  const store = stores.get(String(companyId));
  const months = monthsBetween(from, to);
  if (!months.length) throw new BonusSampleError("To month must be the same as or after From month.");
  if (months.length > 24) throw new BonusSampleError("A run can cover at most 24 months.");
  if (payMode === "part" && !(Number(partPercent) > 0 && Number(partPercent) <= 100)) throw new BonusSampleError("Part % must be more than 0 and at most 100.");
  const cycle = store.cycles.find((c) => c.id === Number(cycleId));
  if (!cycle || cycle.frozen) throw new BonusSampleError("Select an open payroll cycle.");
  const ids = new Set(employeeIds);
  const employees = store.employees.filter((employee) => ids.has(employee.id));
  if (!employees.length) throw new BonusSampleError("No employees selected.");

  // B10: one active run per (type, from, to, cycle, resolved employees).
  const employeeHash = hashIds(employees.map((e) => e.id));
  const existing = store.runs.find((run) => run.status !== "cancelled" && run.status !== "draft" && run.bonus_type_id === type.id
    && run.from_month === from && run.to_month === to && run.payroll_cycle_id === cycle.id && run.employee_hash === employeeHash);
  if (existing) {
    throw new BonusSampleError(`A run for the same bonus type, months, payroll cycle and employees already exists: ${existing.id}.`, { code: "DUPLICATE_RUN", meta: { run_id: existing.id } });
  }

  const rows = employees.map((employee) => calculateEmployee({ employee, type, from, to, payMode, partPercent, rounding, entries: store.entries }));
  return {
    draft_key: `${type.id}|${type.version}|${from}|${to}|${payMode}|${partPercent}|${cycle.id}|${employeeHash}|${Date.now()}`,
    bonus_type_id: type.id,
    type_version: type.version,
    type: clone(type),
    rounding,
    from_month: from,
    to_month: to,
    pay_mode: payMode,
    part_percent: payMode === "part" ? Number(partPercent) : null,
    payroll_cycle_id: cycle.id,
    payroll_cycle: clone(cycle),
    employee_ids: employees.map((e) => e.id),
    rows,
    totals: totalsOf(rows),
    // Source fingerprint (spec B5): the inputs that change the result.
    fingerprint: `${type.id}|${type.version}|${type.modifiedat || ""}|${employeeHash}`,
    calculated_at: nowIso(),
  };
};

// True when the draft must be recalculated before Add to payroll: the type
// changed (new version or edit) or the draft is older than 7 days.
export const draftIsStale = (draft, currentType) => {
  if (!draft || !currentType) return false;
  const fingerprint = `${currentType.id}|${currentType.version}|${currentType.modifiedat || ""}|${draft.fingerprint.split("|")[3]}`;
  const ageDays = (Date.now() - new Date(draft.calculated_at).getTime()) / 86400000;
  return fingerprint !== draft.fingerprint || ageDays > 7;
};

const added = new Map();

// POST runs/{id}/add-to-payroll/ — all entries in one go, or none.
export const addBonusRunToPayroll = async ({ companyId, draft, name, currentType }) => {
  await wait(500);
  // Request key: a retry / double click returns the first result (C14).
  if (added.has(draft.draft_key)) return added.get(draft.draft_key);
  const store = stores.get(String(companyId));
  if (draftIsStale(draft, currentType)) {
    throw new BonusSampleError("Data changed since this was calculated. Recalculate and review before adding to payroll.", { code: "STALE" });
  }
  const months = monthsBetween(draft.from_month, draft.to_month);
  // Re-read the ledger: another run or entry may have been added meanwhile
  // (C51, C56).
  const changed = draft.rows.filter((row) => row.result === "eligible"
    && earlierBonus(store.entries, row.employee_id, draft.bonus_type_id, months).total !== row.already_paid);
  if (changed.length) {
    throw new BonusSampleError(`Earlier bonus changed for ${changed.length} employee(s) since this was calculated. Nothing was added — recalculate and review.`, { code: "LEDGER_CHANGED" });
  }
  const cycle = store.cycles.find((c) => c.id === draft.payroll_cycle_id);
  if (!cycle || cycle.frozen) throw new BonusSampleError("The payroll cycle is no longer open. Go back to Set up and pick an open cycle.");
  const run = createRunRecords(store, {
    type: draft.type, from: draft.from_month, to: draft.to_month, payMode: draft.pay_mode, partPercent: draft.part_percent,
    cycle, rows: draft.rows, name, createdAt: nowIso(), rounding: draft.rounding,
  });
  const created = store.entries.filter((entry) => entry.run_id === run.id).length;
  const outcome = { run_id: run.id, entries_created: created, cycle_name: cycle.name };
  added.set(draft.draft_key, outcome);
  return outcome;
};

// GET runs/{id}/
export const getBonusRun = async ({ companyId, runId }) => {
  await wait(150);
  const store = stores.get(String(companyId));
  const run = store?.runs.find((r) => r.id === runId);
  if (!run) throw new BonusSampleError("Run not found.");
  return clone({ ...run, payroll_cycle: store.cycles.find((c) => c.id === run.payroll_cycle_id) || null, totals: totalsOf(run.rows) });
};

// POST runs/{id}/cancel/ — cancels its Ready entries; blocked once any
// entry is Included or Processed.
export const cancelBonusRun = async ({ companyId, runId, reason }) => {
  await wait();
  if (!String(reason || "").trim()) throw new BonusSampleError("Enter a reason to cancel.");
  const store = stores.get(String(companyId));
  const run = store.runs.find((r) => r.id === runId);
  const entries = store.entries.filter((entry) => entry.run_id === runId);
  if (!run || run.status !== "added" || entries.some((entry) => entry.status === "included" || entry.status === "processed")) {
    throw new BonusSampleError("This run has entries already in payroll, so it can't be cancelled. Use Payroll Adjustments for corrections.");
  }
  const at = nowIso();
  entries.filter((entry) => entry.status === "ready").forEach((entry) => {
    entry.status = "cancelled";
    entry.allocations = {};
    entry.cancel_reason = reason;
    entry.history.push({ at, by: USER, text: `cancelled with run ${runId} — ${reason}` });
  });
  run.status = "cancelled";
  run.cancel_reason = reason;
  return { cancelled: entries.length };
};

// POST entries/{id}/cancel/ — only Ready entries (spec B5, C50).
export const cancelBonusEntry = async ({ companyId, entryId, reason }) => {
  await wait();
  if (!String(reason || "").trim()) throw new BonusSampleError("Enter a reason to cancel.");
  const store = stores.get(String(companyId));
  const entry = store.entries.find((e) => e.id === entryId);
  if (!entry || entry.status !== "ready") {
    throw new BonusSampleError("Only Ready entries can be cancelled. Entries in payroll are corrected through Payroll Adjustments.");
  }
  entry.status = "cancelled";
  entry.allocations = {};
  entry.cancel_reason = reason;
  entry.history.push({ at: nowIso(), by: USER, text: `cancelled — ${reason}` });
  if (entry.run_id) syncRunStatus(store, entry.run_id);
};

// POST entries/{id}/assign-cycle/ — a deferred entry gets an open cycle.
export const assignBonusCycle = async ({ companyId, entryId, cycleId }) => {
  await wait();
  const store = stores.get(String(companyId));
  const entry = store.entries.find((e) => e.id === entryId);
  const cycle = store.cycles.find((c) => c.id === Number(cycleId));
  if (!entry || entry.status !== "ready") throw new BonusSampleError("Only Ready entries can get a payroll cycle.");
  if (!cycle || cycle.frozen) throw new BonusSampleError("Select an open payroll cycle.");
  entry.payroll_cycle_id = cycle.id;
  entry.history.push({ at: nowIso(), by: USER, text: `assigned to ${cycle.name} payroll (paid ${cycle.pay_month})` });
};

// Spec B9 validation for manual and import entries. Returns { errors }.
export const validateEntryValues = (store, values, types) => {
  const errors = {};
  const type = types.find((t) => t.id === Number(values.bonus_type_id));
  const employee = store.employees.find((e) => e.id === Number(values.employee_id));
  if (!employee) errors.employee_id = "Select an employee of this company.";
  if (!type) errors.bonus_type_id = "Select the bonus type.";
  else if (type.status === "Inactive") errors.bonus_type_id = "This bonus type is Inactive.";
  if (!values.period_from) errors.period_from = "Select Period from.";
  if (!values.period_to) errors.period_to = "Select Period to.";
  if (values.period_from && values.period_to && values.period_from > values.period_to) errors.period_to = "Period to must be the same as or after Period from.";
  const amount = String(values.amount ?? "").trim();
  if (!amount) errors.amount = "Enter the amount.";
  else if (!/^\d+(\.\d{1,2})?$/.test(amount)) errors.amount = Number(amount) <= 0 || amount.startsWith("-") ? "Amount must be more than 0." : "Amount can have at most 2 decimals.";
  else if (Number(amount) <= 0) errors.amount = "Amount must be more than 0.";
  if (employee && values.period_from && values.period_to && !errors.period_to
    && !monthsBetween(values.period_from, values.period_to).some((m) => {
      const first = `${m}-01`;
      return (!employee.joined || employee.joined <= `${m}-31`) && (!employee.exited || employee.exited >= first);
    })) {
    errors.employee_id = `${employee.name} was not employed in this period.`;
  }
  if (values.payroll_cycle_id) {
    const cycle = store.cycles.find((c) => c.id === Number(values.payroll_cycle_id));
    if (!cycle || cycle.frozen) errors.payroll_cycle_id = "Select an open payroll cycle.";
  }
  return { errors, type, employee };
};

// POST entries/ (manual) and PATCH entries/{id}/ (manual / import, Ready
// only). Entry and its month ledger change together.
export const saveBonusEntry = async ({ companyId, entryId, values, types, treatment }) => {
  await wait();
  const store = stores.get(String(companyId));
  const { errors, type, employee } = validateEntryValues(store, values, types);
  if (!values.payroll_cycle_id && treatment === "SELECT_CYCLE") errors.payroll_cycle_id = "Payroll cycle required.";
  if (Object.keys(errors).length) throw new BonusSampleError("Check the highlighted fields.", { code: "VALIDATION_ERROR", meta: { fields: errors } });
  const amount = roundHalfUp2(Number(values.amount));
  const at = nowIso();
  if (entryId) {
    const entry = store.entries.find((e) => e.id === entryId);
    if (!entry || entry.status !== "ready" || entry.source === "run") throw new BonusSampleError("Only Ready manual or imported entries can be edited.");
    const before = `${entry.amount} for ${periodText(entry.period_from, entry.period_to)}`;
    Object.assign(entry, {
      employee_id: employee.id, employee_name: employee.name, employee_code: employee.code, site: employee.site,
      bonus_type_id: type.id, bonus_type_name: type.name, type_version: type.version, is_statutory: Boolean(type.is_statutory),
      period_from: values.period_from, period_to: values.period_to, amount,
      payroll_cycle_id: values.payroll_cycle_id ? Number(values.payroll_cycle_id) : null,
      remarks: values.remarks || "",
    });
    entry.allocations = allocate(amount, monthsBetween(entry.period_from, entry.period_to));
    entry.history.push({ at, by: USER, text: `edited — was ₹${before}, now ₹${amount} for ${periodText(entry.period_from, entry.period_to)}` });
    return entry.id;
  }
  const entry = {
    id: `ME-${store.entries.length + 1}-${Date.now() % 10000}`,
    employee_id: employee.id, employee_name: employee.name, employee_code: employee.code, site: employee.site,
    bonus_type_id: type.id, bonus_type_name: type.name, type_version: type.version, is_statutory: Boolean(type.is_statutory),
    period_from: values.period_from, period_to: values.period_to, amount,
    source: "manual", run_id: null, payroll_cycle_id: values.payroll_cycle_id ? Number(values.payroll_cycle_id) : null,
    status: "ready", created_at: at, created_by: USER, remarks: values.remarks || "", external_ref: "", calc: null,
    allocations: allocate(amount, monthsBetween(values.period_from, values.period_to)),
    history: [{ at, by: USER, text: "added manually" }],
  };
  store.entries.push(entry);
  return entry.id;
};

// Import row key (spec B10): company, employee, type, period, amount in
// paise, external ref. Only saved (non-cancelled) entries hold a key.
const dedupKey = (row) => [row.employee_id, row.bonus_type_id, row.period_from, row.period_to, Math.round(Number(row.amount) * 100), row.external_ref || ""].join("|");

// POST import/preview/ — rows split into Valid / Invalid / Duplicate.
// rows: [{ row, employee_code, bonus_type, period_from, period_to, amount, payroll_cycle, remarks, external_ref }]
export const previewBonusImport = async ({ companyId, rows, types, treatment }) => {
  await wait(300);
  const store = stores.get(String(companyId));
  const saved = new Set(store.entries.filter((e) => e.status !== "cancelled" && e.source !== "run").map(dedupKey));
  const seen = new Set();
  return rows.map((raw) => {
    const employee = store.employees.find((e) => e.code.toLowerCase() === String(raw.employee_code || "").trim().toLowerCase());
    const type = types.find((t) => String(t.name).toLowerCase() === String(raw.bonus_type || "").trim().toLowerCase());
    const cycleText = String(raw.payroll_cycle || "").trim();
    const cycle = cycleText ? store.cycles.find((c) => c.name.toLowerCase() === cycleText.toLowerCase() || c.month === cycleText) : null;
    const values = {
      employee_id: employee?.id, bonus_type_id: type?.id, period_from: raw.period_from, period_to: raw.period_to,
      amount: raw.amount, payroll_cycle_id: cycle?.id || null, remarks: raw.remarks || "", external_ref: raw.external_ref || "",
    };
    const { errors } = validateEntryValues(store, values, types);
    if (!employee) errors.employee_id = `Employee code ${raw.employee_code || "(blank)"} not found.`;
    if (!type) errors.bonus_type_id = `Bonus type "${raw.bonus_type || ""}" not found.`;
    if (cycleText && !cycle) errors.payroll_cycle_id = `Payroll cycle "${cycleText}" not found.`;
    else if (cycle && cycle.frozen) errors.payroll_cycle_id = `Payroll cycle ${cycle.name} is not open.`;
    if (!cycleText && treatment === "SELECT_CYCLE") errors.payroll_cycle_id = "Payroll cycle required.";
    // No cycle in the file: Current -> current open cycle; Defer -> none.
    if (!cycleText && treatment === "CURRENT_CYCLE") values.payroll_cycle_id = store.cycles.find((c) => !c.frozen)?.id || null;
    const base = {
      row: raw.row, employee_code: raw.employee_code, employee_name: employee?.name || "", bonus_type: type?.name || raw.bonus_type,
      period_from: raw.period_from, period_to: raw.period_to, amount: raw.amount, remarks: raw.remarks || "",
      payroll_cycle_name: values.payroll_cycle_id ? store.cycles.find((c) => c.id === values.payroll_cycle_id)?.name : "Deferred",
      values, is_statutory: Boolean(type?.is_statutory),
    };
    if (Object.keys(errors).length) return { ...base, status: "invalid", reason: Object.values(errors).join(" ") };
    const key = dedupKey({ ...values, amount: Number(values.amount) });
    if (saved.has(key) || seen.has(key)) return { ...base, status: "duplicate", reason: saved.has(key) ? "Already saved — not saved again." : "Same row appears twice in this file." };
    seen.add(key);
    return { ...base, status: "valid", reason: "" };
  });
};

// POST import/commit/ — saves the valid rows (partial success kept).
export const commitBonusImport = async ({ companyId, rows, types, fileName }) => {
  await wait(400);
  const store = stores.get(String(companyId));
  const at = nowIso();
  let saved = 0;
  rows.filter((row) => row.status === "valid").forEach((row) => {
    const { values } = row;
    const type = types.find((t) => t.id === values.bonus_type_id);
    const employee = store.employees.find((e) => e.id === values.employee_id);
    const amount = roundHalfUp2(Number(values.amount));
    store.entries.push({
      id: `IE-${store.entries.length + 1}-${saved}`,
      employee_id: employee.id, employee_name: employee.name, employee_code: employee.code, site: employee.site,
      bonus_type_id: type.id, bonus_type_name: type.name, type_version: type.version, is_statutory: Boolean(type.is_statutory),
      period_from: values.period_from, period_to: values.period_to, amount,
      source: "import", import_ref: `${fileName} · row ${row.row}`, external_ref: values.external_ref || "",
      run_id: null, payroll_cycle_id: values.payroll_cycle_id || null, status: "ready", created_at: at, created_by: USER,
      remarks: values.remarks || "", calc: null,
      allocations: allocate(amount, monthsBetween(values.period_from, values.period_to)),
      history: [{ at, by: USER, text: `imported from ${fileName}${values.payroll_cycle_id ? "" : " (no payroll cycle; default treatment Defer)"}` }],
    });
    saved += 1;
  });
  return { saved };
};

// Month helpers re-exported for the screens.
export { monthOfDate };
